import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'

export interface PageHeaderProps {
  /**
   * Заголовок страницы
   */
  title: string
  /**
   * Подзаголовок (опционально)
   */
  subtitle?: string
  /**
   * Путь для кнопки назад (по умолчанию history.back())
   */
  backTo?: string
  /**
   * Callback при нажатии назад
   */
  onBack?: () => void
  /**
   * Скрыть кнопку назад (когда используется нативная кнопка Telegram)
   */
  hideBackButton?: boolean
  /**
   * Дополнительные действия справа
   */
  rightAction?: React.ReactNode
  /**
   * Прозрачный фон (для страниц с hero-секцией)
   */
  transparent?: boolean
  /**
   * Дополнительные классы
   */
  className?: string
}

/**
 * UI компонент: Фиксированная шапка страницы
 * 
 * Премиальный header с кнопкой назад в стиле iOS.
 * Fixed позиционирование для корректной работы scroll.
 * 
 * @example
 * ```tsx
 * <PageHeader 
 *   title="Избранное" 
 *   subtitle="12 товаров"
 *   backTo="/profile" 
 * />
 * ```
 */
export function PageHeader({
  title,
  subtitle,
  backTo,
  onBack,
  hideBackButton = false,
  rightAction,
  transparent = false,
  className,
}: PageHeaderProps) {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()

  const handleBack = () => {
    hapticFeedback.impactOccurred('light')
    
    if (onBack) {
      onBack()
      return
    }
    
    if (backTo) {
      navigate({ to: backTo })
      return
    }
    
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: '/' })
    }
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-30',
        // На desktop учитываем SideNav
        'lg:left-[240px]',
        // Стили шапки
        'flex items-center gap-3 px-4 lg:px-6 h-14',
        // Фон
        !transparent && 'bg-paper/95 dark:bg-[#0F1113]/95 backdrop-blur-md',
        !transparent && 'border-b border-gray-100/50 dark:border-dark-border/30',
        className
      )}
      style={{
        paddingTop: 'calc(var(--top-inset, 0px) + 12px)',
      }}
    >
      {/* Кнопка назад (скрывается если используется нативная кнопка Telegram) */}
      {!hideBackButton && (
        <button
          onClick={handleBack}
          className={cn(
            'flex items-center justify-center',
            'w-10 h-10 -ml-2',
            'rounded-full',
            'text-ink dark:text-white',
            'hover:bg-gray-100 dark:hover:bg-dark-elevated',
            'active:scale-95',
            'transition-all duration-150',
            'touch-target'
          )}
          aria-label="Назад"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      )}

      {/* Заголовок и подзаголовок */}
      <div className="flex-1 min-w-0">
        <h1 className="text-lg font-semibold text-ink dark:text-white truncate">
          {title}
        </h1>
        {subtitle && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {subtitle}
          </p>
        )}
      </div>

      {/* Действия справа */}
      {rightAction && (
        <div className="flex items-center gap-2">
          {rightAction}
        </div>
      )}
    </header>
  )
}

/**
 * Spacer для компенсации высоты фиксированной шапки
 * Добавляйте после PageHeader в контент
 */
export function PageHeaderSpacer() {
  return (
    <div 
      className="h-14" 
      style={{ 
        marginTop: 'env(safe-area-inset-top, 0px)' 
      }} 
      aria-hidden="true" 
    />
  )
}

